/**
 * Staff deactivation — F-CTRL-N76 (2026-10-17).
 *
 * Firing someone is a status change, never a delete: the order history,
 * void log and house-account ledger all keep their name, so the backend
 * refuses to delete any account with history (`user_has_orders`,
 * `user_has_house_account_history`). Flipping `is_active` to false on
 * `PUT /admin/users/:id` is what actually ends their access — the backend
 * stamps `token_revoked_at` (every live session dies on its next request),
 * login and manager-PIN identification both filter on `is_active = true`,
 * and their meal allowance is paused (F-MM-N31).
 *
 * The pieces here are the pure decisions the staff screen makes around
 * that call, kept out of the component so they are testable.
 */

export type StaffStatusActor = 'admin' | 'manager' | string | null | undefined

/** The delete refusals that mean "there is history — deactivate instead". */
const HISTORY_REFUSALS = new Set(['user_has_orders', 'user_has_house_account_history'])

export function deleteRefusalOffersDeactivate(error: { code?: string } | null | undefined): boolean {
  return !!error?.code && HISTORY_REFUSALS.has(error.code)
}

export type StaffListParams = { page: number; per_page: number; search?: string; active?: 'true' }

/**
 * Query for the staff list. Former staff are hidden unless asked for —
 * the "archive". Named the way `getAdminUsers` actually reads them:
 * `active` (string flag) and `per_page` — the screen used to send `limit`,
 * which the backend ignores, so every page came back 20 rows long.
 */
export function staffListParams(input: {
  page: number
  limit: number
  search: string
  showFormer: boolean
}): StaffListParams {
  const params: StaffListParams = {
    page: input.page,
    per_page: input.limit,
    search: input.search || undefined,
  }
  if (!input.showFormer) params.active = 'true'
  return params
}

/**
 * Why the actor may not change this person's status, or null when they may.
 * Mirrors the backend gate on user mutation (`adminManagerWrite` +
 * `rejectManagerMutatingAdmin`), plus "never yourself" so an admin cannot
 * lock the tenant out of its own admin from the list.
 */
export function staffStatusActionBlock(input: {
  actorRole: StaffStatusActor
  actorId: string | null | undefined
  target: { id: string; role: string }
}): string | null {
  const { actorRole, actorId, target } = input
  if (actorRole !== 'admin' && actorRole !== 'manager') {
    return 'Only an admin or manager can change a staff member’s status.'
  }
  if (actorId && String(target.id) === String(actorId)) {
    return 'You cannot deactivate your own account.'
  }
  if (actorRole === 'manager' && target.role === 'admin') {
    return 'A manager cannot change an admin’s status.'
  }
  return null
}

/** Confirmation copy: exactly what deactivating does, and what it keeps. */
export function deactivateConfirmText(firstName: string, lastName: string): string {
  const name = `${firstName} ${lastName}`.trim()
  return (
    `${name} will be signed out everywhere and will not be able to log in or use their manager PIN. ` +
    `Their meal allowance is paused. Their name stays on past orders, voids and reports. ` +
    `You can reactivate them later.`
  )
}
